// src/components/home/PostCard.jsx
import React, {
  useState, useMemo, useEffect, useRef, useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import {
  doc, updateDoc, arrayUnion, arrayRemove, increment, deleteDoc,
  addDoc, collection, serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import {
  formatRelativeTime,
  formatCount,
  normalizeMedia,
  getFontClass,
  POST_BG_PRESETS,
} from '../../utils/feedHelpers';
import { sanitizeHtml, htmlToPlainText } from '../../utils/sanitizeHtml';
import CommentList from './CommentList';
import ShareModal from './ShareModal';
import PostEditor from './PostEditor';

/* ================================================================== */
/* URL detection & clickable links                                     */
/* ================================================================== */

const URL_REGEX =
  /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,!?;:'"]|[a-z0-9.-]+\.(?:com|org|net|io|gov|edu|co|ph|sa|uk|us|info|biz|dev|app|xyz)(?:\/[^\s<>()]*)?)/gi;

const renderContentWithLinks = (text, extraCls = '') => {
  if (!text) return null;

  const re = new RegExp(URL_REGEX.source, 'gi');
  const parts = text.split(re).filter((p) => p !== undefined);

  return parts.map((part, i) => {
    if (!part) return null;

    re.lastIndex = 0;
    const isUrl = re.test(part);
    re.lastIndex = 0;

    if (isUrl) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline font-medium break-all ${
            extraCls || 'text-orange hover:opacity-80'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

/* ================================================================== */
/* Confirm modal                                                       */
/* ================================================================== */

const ConfirmModal = ({
  open, title, message, confirmLabel = 'Confirm', danger = true,
  busy = false, onConfirm, onCancel,
}) => {
  useEffect(() => {
    if (!open || busy) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full sm:rounded-2xl sm:max-w-sm max-h-full overflow-y-auto p-6 rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              danger ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange/10'
            }`}
          >
            <i
              className={`fas fa-triangle-exclamation ${
                danger ? 'text-red-500' : 'text-orange'
              }`}
              aria-hidden="true"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition ${
              danger ? 'bg-red-500 hover:bg-red-600' : 'bg-orange hover:bg-orange-dark'
            }`}
          >
            {busy && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Reject modal                                                        */
/* ================================================================== */

const RejectModal = ({ open, busy, onCancel, onSubmit }) => {
  const [reason, setReason] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open || busy) return undefined;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full sm:rounded-2xl sm:max-w-sm max-h-full overflow-y-auto p-6 rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
          Reject this post?
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          The post will be deleted. You can optionally include a reason for the
          author.
        </p>
        <label
          htmlFor="reject-reason"
          className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
        >
          Reason (optional)
        </label>
        <textarea
          ref={inputRef}
          id="reject-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          placeholder="e.g., Please keep content respectful."
          className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange resize-none disabled:opacity-50"
        />
        <div className="flex gap-3 justify-end mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={busy}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition"
          >
            {busy && <i className="fas fa-spinner fa-spin" aria-hidden="true" />}
            Reject &amp; Delete
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Visibility badge                                                    */
/* ================================================================== */

const VisibilityBadge = ({ visibility }) => {
  const vis = String(visibility || 'public').toLowerCase();
  if (vis === 'public') return null;

  const isOnlyMe = vis === 'onlyme' || vis === 'only_me';

  const styles = isOnlyMe
    ? {
        cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        icon: 'fa-lock',
        label: 'Only Me',
        title: 'Only you and admins can see this',
      }
    : {
        cls: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
        icon: 'fa-users',
        label: 'Members',
        title: 'Only logged-in members can see this',
      };

  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${styles.cls}`}
      title={styles.title}
    >
      <i className={`fas ${styles.icon} text-[8px]`} aria-hidden="true" />
      {styles.label}
    </span>
  );
};

/* ================================================================== */
/* Backdated badge                                                     */
/* ================================================================== */

const BackdatedBadge = ({ postDate }) => {
  if (!postDate) return null;

  const d =
    postDate?.toDate?.() ||
    (typeof postDate === 'number' ? new Date(postDate) : null) ||
    (typeof postDate === 'string' ? new Date(postDate) : null);

  if (!d || Number.isNaN(d.getTime?.())) return null;

  const isThisYear = d.getFullYear() === new Date().getFullYear();
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange/10 text-orange"
      title={`Posted: ${d.toLocaleString()}`}
    >
      <i className="fas fa-clock-rotate-left text-[8px]" aria-hidden="true" />
      {d.toLocaleDateString('en', {
        month: 'short',
        day: 'numeric',
        ...(isThisYear ? {} : { year: 'numeric' }),
      })}
    </span>
  );
};

/* ================================================================== */
/* Heart burst animation (double-tap like)                             */
/* ================================================================== */

const HeartBurst = ({ show }) => {
  if (!show) return null;
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
      aria-hidden="true"
    >
      <div
        className="text-white opacity-90"
        style={{
          animation: 'heartBurst 0.7s cubic-bezier(0.17, 0.89, 0.32, 1.49) forwards',
        }}
      >
        <i className="fas fa-heart text-8xl drop-shadow-2xl" />
      </div>
      <style>{`
        @keyframes heartBurst {
          0% { transform: scale(0); opacity: 0; }
          30% { transform: scale(1.2); opacity: 1; }
          60% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.1); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

/* ================================================================== */
/* Media carousel                                                      */
/* ================================================================== */

const MediaCarousel = ({
  orderedMedia,
  mediaIndex,
  setMediaIndex,
  onOpenMedia,
  onDoubleTapLike,
  isVideo,
}) => {
  const activeMedia = orderedMedia[mediaIndex];
  const hasMultiple = orderedMedia.length > 1;
  const [heartVisible, setHeartVisible] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  const touchStartRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef(0);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e) => {
    if (!hasMultiple) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;

    // Horizontal swipe (must be more horizontal than vertical)
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      if (dx < 0 && mediaIndex < orderedMedia.length - 1) {
        setMediaIndex(mediaIndex + 1);
      } else if (dx > 0 && mediaIndex > 0) {
        setMediaIndex(mediaIndex - 1);
      }
    }
  };

  const handleMediaClick = () => {
    // Double-tap to like (only for images)
    if (!isVideo) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        // Double tap detected
        setHeartVisible(true);
        onDoubleTapLike?.();
        setTimeout(() => setHeartVisible(false), 700);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
    }
    // Single click → open lightbox
    onOpenMedia?.();
  };

  return (
    <div
      className="relative bg-black group/media select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {isVideo ? (
        <video
          key={activeMedia.url}
          src={activeMedia.url}
          controls
          playsInline
          className="w-full max-h-[600px] object-contain"
          preload="metadata"
          onDoubleClick={(e) => {
            e.preventDefault();
            onOpenMedia?.();
          }}
        />
      ) : (
        <>
          {!mediaLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <i
                className="fas fa-spinner fa-spin text-white/40 text-2xl"
                aria-hidden="true"
              />
            </div>
          )}
          <img
            key={activeMedia.url}
            src={activeMedia.url}
            alt=""
            className="w-full max-h-[600px] object-contain cursor-zoom-in"
            loading="lazy"
            onClick={handleMediaClick}
            onLoad={() => setMediaLoaded(true)}
          />
        </>
      )}

      <HeartBurst show={heartVisible} />

      {/* Zoom hint on image hover */}
      {activeMedia?.type === 'image' && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover/media:opacity-100 transition-opacity"
          aria-hidden="true"
        >
          <div className="bg-black/60 text-white rounded-full p-3 backdrop-blur-sm">
            <i className="fas fa-expand text-lg" />
          </div>
        </div>
      )}

      {/* Cover badge */}
      {activeMedia?.isCover && orderedMedia.length > 1 && (
        <div className="absolute top-3 left-3 bg-orange text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
          <i className="fas fa-star text-[9px]" aria-hidden="true" />
          Cover
        </div>
      )}

      {/* Video hint */}
      {activeMedia?.type === 'video' && (
        <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] rounded-full px-2 py-1 opacity-80">
          <i className="fas fa-expand mr-1" aria-hidden="true" />
          Double-click to view full
        </div>
      )}

      {hasMultiple && (
        <>
          {mediaIndex > 0 && (
            <button
              type="button"
              onClick={() => setMediaIndex(mediaIndex - 1)}
              aria-label="Previous media"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 z-10 transition active:scale-90"
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
          )}
          {mediaIndex < orderedMedia.length - 1 && (
            <button
              type="button"
              onClick={() => setMediaIndex(mediaIndex + 1)}
              aria-label="Next media"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 z-10 transition active:scale-90"
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          )}
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs rounded-full px-2 py-1 z-10">
            {mediaIndex + 1}/{orderedMedia.length}
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {orderedMedia.map((m, i) => (
              <button
                key={`dot-${i}`}
                type="button"
                onClick={() => setMediaIndex(i)}
                aria-label={`Go to media ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === mediaIndex ? 'bg-white scale-125' : 'bg-white/40'
                } ${m.type === 'video' ? 'ring-1 ring-white/70' : ''}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ================================================================== */
/* PostCard                                                            */
/* ================================================================== */

const PostCard = ({
  post,
  onDeleted,
  onUpdated,
  onMediaClick,
  isGuest = false,
}) => {
  const { user, isAdmin } = useAuth();

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const [optimisticLike, setOptimisticLike] = useState(null);
  const [likePulse, setLikePulse] = useState(false);

  const liked =
    optimisticLike !== null
      ? optimisticLike
      : Boolean(user?.uid && post.likedBy?.includes(user.uid));

  const canEdit = !isGuest && (post.authorId === user?.uid || isAdmin);
  const canDelete = !isGuest && (post.authorId === user?.uid || isAdmin);
  const isPending = post.approved === false;
  const isAuthor = post.authorId === user?.uid;
  const canModerate = !isGuest && isAdmin && isPending;
  const interactionLocked = isPending && !isAuthor && !isAdmin;

  const commentRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ---------------- Media ordering ---------------- */
  const media = useMemo(() => normalizeMedia(post), [post]);

  const orderedMedia = useMemo(() => {
    if (!media.length) return media;
    const coverIdx = media.findIndex((m) => m.isCover);
    if (coverIdx <= 0) return media;
    return [media[coverIdx], ...media.filter((_, i) => i !== coverIdx)];
  }, [media]);

  useEffect(() => {
    if (mediaIndex >= orderedMedia.length) {
      setMediaIndex(Math.max(0, orderedMedia.length - 1));
    }
  }, [orderedMedia.length, mediaIndex]);

  useEffect(() => {
    setMediaIndex(0);
  }, [post.id]);

  /* ---------------- Design background & font ---------------- */
  const bgPreset = useMemo(
    () =>
      POST_BG_PRESETS.find(
        (p) => p.id === (post.design?.bgPreset || 'default'),
      ),
    [post.design?.bgPreset],
  );
  const hasBg = bgPreset && bgPreset.id !== 'default';

  const fontClass = useMemo(
    () => getFontClass(post.design?.font),
    [post.design?.font],
  );

  const fontAlign =
    post.design?.font?.align === 'left'
      ? 'justify-start text-left'
      : post.design?.font?.align === 'right'
      ? 'justify-end text-right'
      : 'justify-center text-center';

  const displayDate = post.postDate || post.createdAt;

  /* ---------------- Content rendering ---------------- */
  const richHtml = useMemo(() => {
    if (!post.contentHtml) return '';
    return sanitizeHtml(post.contentHtml);
  }, [post.contentHtml]);

  const plainText = post.content || '';
  const hasRich = Boolean(richHtml && htmlToPlainText(richHtml).trim());
  const hasPlain = Boolean(plainText && plainText.trim());
  const hasContent = hasRich || hasPlain;

  const linkClsInBg = 'text-white underline opacity-90 hover:opacity-100';

  const showEdited =
    post.editedAt &&
    (typeof post.editedAt?.toDate === 'function' ||
      typeof post.editedAt?.seconds === 'number');

  const openMedia = useCallback(
    (index = mediaIndex) => {
      onMediaClick?.(orderedMedia, index);
    },
    [onMediaClick, orderedMedia, mediaIndex],
  );

  /* ---------------- Like ---------------- */
  const toggleLike = async () => {
    if (!user || busy || interactionLocked) return;

    const next = !liked;
    setOptimisticLike(next);
    setLikePulse(true);
    setTimeout(() => setLikePulse(false), 300);
    setBusy(true);
    try {
      const ref = doc(db, 'posts', post.id);
      if (next) {
        await updateDoc(ref, {
          likedBy: arrayUnion(user.uid),
          likeCount: increment(1),
        });
      } else {
        await updateDoc(ref, {
          likedBy: arrayRemove(user.uid),
          likeCount: increment(-1),
        });
      }
    } catch (err) {
      console.error('like failed:', err);
      if (mountedRef.current) setOptimisticLike(!next);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /* ---------------- Delete ---------------- */
  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteDoc(doc(db, 'posts', post.id));
      onDeleted?.(post.id);
    } catch (err) {
      console.error('delete post failed:', err);
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        setConfirmDeleteOpen(false);
      }
    }
  };

  /* ---------------- Approve ---------------- */
  const approve = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'posts', post.id), {
        approved: true,
        approvalStatus: 'approved',
        approvedBy: user.uid,
        approvedAt: serverTimestamp(),
      });

      try {
        await addDoc(collection(db, 'notifications'), {
          title: '✅ Post Approved',
          body: `Your post${
            post.content
              ? ` "${post.content.slice(0, 60)}${
                  post.content.length > 60 ? '…' : ''
                }"`
              : ''
          } has been approved and is now visible to everyone.`,
          recipientType: 'users',
          recipients: [post.authorId],
          createdAt: serverTimestamp(),
          readBy: [],
          link: '/',
          type: 'post_approved',
        });
      } catch (notifyErr) {
        console.warn('Notification failed:', notifyErr);
      }
    } catch (err) {
      console.error('approve failed:', err);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /* ---------------- Reject ---------------- */
  const submitReject = async (reason) => {
    if (busy) return;
    setBusy(true);
    try {
      try {
        await addDoc(collection(db, 'notifications'), {
          title: '❌ Post Not Approved',
          body: `Your post was not approved.${
            reason ? ` Reason: ${reason}` : ''
          }`,
          recipientType: 'users',
          recipients: [post.authorId],
          createdAt: serverTimestamp(),
          readBy: [],
          link: '/',
          type: 'post_rejected',
        });
      } catch (notifyErr) {
        console.warn('Notification failed:', notifyErr);
      }

      await deleteDoc(doc(db, 'posts', post.id));
      onDeleted?.(post.id);
      if (mountedRef.current) setRejectOpen(false);
    } catch (err) {
      console.error('reject failed:', err);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /* ---------------- Comments scroll into view ---------------- */
  useEffect(() => {
    if (commentsOpen && commentRef.current) {
      setTimeout(() => {
        commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
    }
  }, [commentsOpen]);

  const hideCountsForPending = isPending && !isAuthor && !isAdmin;

  return (
    <>
      <article
        className="bg-white dark:bg-gray-800 rounded-xl shadow mb-4 overflow-hidden"
        aria-busy={busy}
      >
        {/* Pending banner for author */}
        {isPending && isAuthor && !isAdmin && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2 flex items-center gap-2">
            <i
              className="fas fa-clock text-yellow-600 text-sm animate-pulse"
              aria-hidden="true"
            />
            <span className="text-xs text-yellow-800 dark:text-yellow-200">
              Your post is <strong>pending admin approval</strong> — only you can
              see it.
            </span>
          </div>
        )}

        {/* Pending moderation bar for admin */}
        {canModerate && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2 flex items-center gap-2 flex-wrap">
            <i
              className="fas fa-shield-halved text-yellow-600 text-sm"
              aria-hidden="true"
            />
            <span className="text-xs text-yellow-800 dark:text-yellow-200 flex-1 min-w-0 truncate">
              Pending approval from <strong>{post.authorName}</strong>
            </span>
            <button
              type="button"
              onClick={approve}
              disabled={busy}
              className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded font-medium disabled:opacity-50 transition active:scale-95"
            >
              <i className="fas fa-check mr-1" aria-hidden="true" />
              Approve
            </button>
            <button
              type="button"
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded font-medium disabled:opacity-50 transition active:scale-95"
            >
              <i className="fas fa-times mr-1" aria-hidden="true" />
              Reject
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 p-4">
          {isGuest ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold overflow-hidden flex-shrink-0">
              {post.authorPhoto ? (
                <img
                  src={post.authorPhoto}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                post.authorName?.[0]?.toUpperCase() || 'U'
              )}
            </div>
          ) : (
            <Link
              to={`/profile/${post.authorId}`}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-orange/30 transition active:scale-95"
            >
              {post.authorPhoto ? (
                <img
                  src={post.authorPhoto}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                post.authorName?.[0]?.toUpperCase() || 'U'
              )}
            </Link>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                {post.authorName}
              </p>
              <VisibilityBadge visibility={post.visibility} />
              {post.backdated && isAdmin && (
                <BackdatedBadge postDate={post.postDate} />
              )}
              {showEdited && (
                <span className="text-[10px] text-gray-400 italic">
                  · edited
                </span>
              )}
            </div>
            <p
              className="text-xs text-gray-500"
              title={new Date(
                displayDate?.toDate?.()?.getTime?.() || Date.now(),
              ).toLocaleString()}
            >
              {formatRelativeTime(displayDate)}
            </p>
          </div>

          {/* Edit + Delete buttons */}
          {canEdit && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setEditorOpen(true)}
                aria-label="Edit post"
                title="Edit post"
                className="text-gray-400 hover:text-blue-500 p-2 transition active:scale-90"
              >
                <i className="fas fa-pen text-sm" aria-hidden="true" />
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={busy}
                  aria-label="Delete post"
                  title="Delete post"
                  className="text-gray-400 hover:text-red-500 p-2 disabled:opacity-50 transition active:scale-90"
                >
                  <i className="fas fa-trash-alt text-sm" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {hasContent && (
          <div className={`px-4 pb-3 ${hasBg ? 'pt-1' : ''}`}>
            {hasBg ? (
              <div
                className={`rounded-2xl px-5 py-6 min-h-[120px] flex ${fontAlign} ${bgPreset.bg} ${bgPreset.text}`}
              >
                {hasRich ? (
                  <div
                    className={`${fontClass} whitespace-pre-wrap break-words w-full post-rich-content`}
                    dangerouslySetInnerHTML={{ __html: richHtml }}
                  />
                ) : (
                  <p
                    className={`${fontClass} whitespace-pre-wrap break-words w-full`}
                  >
                    {renderContentWithLinks(plainText, linkClsInBg)}
                  </p>
                )}
              </div>
            ) : hasRich ? (
              <div
                className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words post-rich-content"
                dangerouslySetInnerHTML={{ __html: richHtml }}
              />
            ) : (
              <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
                {renderContentWithLinks(plainText)}
              </p>
            )}
          </div>
        )}

        {/* Media */}
        {orderedMedia.length > 0 && (
          <MediaCarousel
            orderedMedia={orderedMedia}
            mediaIndex={mediaIndex}
            setMediaIndex={setMediaIndex}
            onOpenMedia={openMedia}
            onDoubleTapLike={toggleLike}
            isVideo={orderedMedia[mediaIndex]?.type === 'video'}
          />
        )}

        {/* Counts */}
        {!hideCountsForPending &&
          (post.likeCount > 0 ||
            post.commentCount > 0 ||
            post.shareCount > 0) && (
            <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-500">
              <div
                className={`flex items-center gap-1 transition-transform ${
                  likePulse ? 'scale-110' : 'scale-100'
                }`}
                aria-live="polite"
              >
                {post.likeCount > 0 && (
                  <>
                    <span className="w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <i
                        className="fas fa-heart text-[8px]"
                        aria-hidden="true"
                      />
                    </span>
                    <span>{formatCount(post.likeCount)}</span>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                {post.commentCount > 0 && !isGuest && (
                  <button
                    type="button"
                    onClick={() => setCommentsOpen((o) => !o)}
                    className="hover:underline"
                  >
                    {formatCount(post.commentCount)} comments
                  </button>
                )}
                {post.commentCount > 0 && isGuest && (
                  <span>{formatCount(post.commentCount)} comments</span>
                )}
                {post.shareCount > 0 && (
                  <span>{formatCount(post.shareCount)} shares</span>
                )}
              </div>
            </div>
          )}

        {/* Actions */}
        <div className="px-2 py-1 border-t dark:border-gray-700 flex">
          {isGuest ? (
            <Link
              to="/login"
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium text-orange transition"
            >
              <i className="fas fa-sign-in-alt" aria-hidden="true" />
              Sign in to like, comment &amp; share
            </Link>
          ) : (
            <>
              <button
                type="button"
                onClick={toggleLike}
                disabled={busy || interactionLocked}
                aria-pressed={liked}
                title={
                  interactionLocked
                    ? 'Pending posts cannot be liked yet'
                    : liked
                    ? 'Unlike'
                    : 'Like'
                }
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium transition active:scale-95 ${
                  liked ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'
                } disabled:opacity-60`}
              >
                <i
                  className={`${liked ? 'fas' : 'far'} fa-heart text-lg transition-transform ${
                    likePulse ? 'scale-125' : 'scale-100'
                  }`}
                  aria-hidden="true"
                />
                Like
              </button>
              <button
                type="button"
                onClick={() => setCommentsOpen((o) => !o)}
                disabled={interactionLocked}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 transition disabled:opacity-60 active:scale-95"
              >
                <i className="far fa-comment text-lg" aria-hidden="true" />
                Comment
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                disabled={interactionLocked}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 transition disabled:opacity-60 active:scale-95"
              >
                <i className="fas fa-share text-lg" aria-hidden="true" />
                Share
              </button>
            </>
          )}
        </div>

        {!isGuest && (
          <div ref={commentRef} className="px-4 pb-4">
            <CommentList postId={post.id} open={commentsOpen} />
          </div>
        )}

        {!isGuest && shareOpen && (
          <ShareModal post={post} onClose={() => setShareOpen(false)} />
        )}
      </article>

      {/* Delete confirmation */}
      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete this post?"
        message="This action cannot be undone. The post and its media will be permanently removed."
        confirmLabel="Delete"
        danger
        busy={busy}
        onConfirm={doDelete}
        onCancel={() => !busy && setConfirmDeleteOpen(false)}
      />

      {/* Reject reason */}
      <RejectModal
        open={rejectOpen}
        busy={busy}
        onCancel={() => !busy && setRejectOpen(false)}
        onSubmit={submitReject}
      />

      {/* Post Editor Modal */}
      {editorOpen && !isGuest && (
        <PostEditor
          post={post}
          onClose={() => setEditorOpen(false)}
          onUpdated={(updates) => {
            onUpdated?.(post.id, updates);
            setEditorOpen(false);
          }}
          onDeleted={() => {
            onDeleted?.(post.id);
            setEditorOpen(false);
          }}
        />
      )}
    </>
  );
};

export default PostCard;